import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Toaster, toast } from 'react-hot-toast';
import Pagination from 'components/pagination';
import { apiCall } from 'utils/apiClient';

const UnregisteredPartners = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const ApiFetch = async () => {
    setLoading(true);
    const body = {
      search: debouncedSearch
    };

    try {
      const res = await apiCall({
        endpoint: "admin/partner/unregistered",
        method: "GET",
        data: body,
      });

      if (res.status === 200) {
        setData(res.data || []);
      }
    } catch (error) {
      console.log('error', error);
      toast.error('Failed to fetch unregistered partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ApiFetch();
  }, [debouncedSearch]);

  const filteredData = data.filter((item) => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      (item?.name || '').toLowerCase().includes(query) ||
      (item?.mobile || '').toLowerCase().includes(query) ||
      (item?.profession || item?.work || '').toLowerCase().includes(query) ||
      (item?.service_areas || item?.area || '').toLowerCase().includes(query)
    );
  });

  const totalRecords = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + recordsPerPage);
  const displayedCount = Math.min(currentPage * recordsPerPage, totalRecords);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleRegisterClick = (partner) => {
    navigate('/add-partner', {
      state: {
        prefillMobile: partner?.mobile || '',
      },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="border-solid border-2 mt-6 bg-white rounded-lg shadow-sm">
        <div className="text-lg font-semibold px-8 pt-6">Unregistered Partners</div>
        <div className="px-8 text-xs text-gray-500 mt-0.5">
            Partners who have generated OTP but haven&apos;t completed registration
        </div>

        {/* Search & Action Bar */}
        <div className="flex flex-wrap items-center justify-between px-8 py-3 gap-3">
          <input
            type="text"
            placeholder="Search Unregistered Partner..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2 w-full sm:w-1/2"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="px-8 py-8 text-center text-sm text-gray-500">Loading unregistered partners...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left border rounded-md">
                <thead className="border-b bg-gray-100 font-medium text-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-center">Sr.No</th>
                    <th className="px-4 py-3 text-center">Mobile</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-center text-gray-800">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="py-6 text-center text-gray-500">
                        No unregistered partners found
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {startIndex + index + 1}
                        </td>
                        <td className="px-4 py-3">{item.mobile}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                              <button
                              onClick={() => handleRegisterClick(item)}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Register
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
                recordsPerPage={recordsPerPage}
                onRecordsPerPageChange={(val) => {
                  setCurrentPage(1);
                  setRecordsPerPage(val);
                }}
                totalRecords={totalRecords}
                count={displayedCount}
              showFirstLastControls
              compact
              />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UnregisteredPartners;
